import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const ATTEMPTS = 3;
const BACKOFF_MS = 900;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class GoneError extends Error {}

const resolveId = async (articleId, slug) => {
  if (articleId) return articleId;
  const resp = await fetch(`${API}/api/blog/articles?limit=50&offset=0`);
  if (!resp.ok) throw new Error("Failed to load");
  const { articles } = await resp.json();
  const match = articles.find((a) => a.slug === slug);
  // Not in the published list: either retired or never existed.
  if (!match) throw new GoneError("Article not available");
  return match.id;
};

const fetchArticle = async (articleId, slug) => {
  const id = await resolveId(articleId, slug);
  const resp = await fetch(`${API}/api/blog/articles/${id}`);
  if (resp.status === 410 || resp.status === 404) throw new GoneError("Article not available");
  if (!resp.ok) throw new Error("Failed to load");
  return resp.json();
};

export default function useBlogArticle(articleId, slug) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setGone(false);

      for (let attempt = 1; attempt <= ATTEMPTS; attempt += 1) {
        try {
          const data = await fetchArticle(articleId, slug);
          if (cancelled) return;
          setArticle(data);
          setLoading(false);
          return;
        } catch (err) {
          if (cancelled) return;
          if (err instanceof GoneError) {
            setGone(true);
            setLoading(false);
            return;
          }
          if (attempt === ATTEMPTS) {
            setError(err.message);
            setLoading(false);
            return;
          }
          await wait(BACKOFF_MS * attempt);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [articleId, slug]);

  return { article, loading, error, gone };
}
